import { msg, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import Container from "../../../common/Container";
import Footer from "../../../common/Footer";
import FooterButton from "../../../common/FooterButton";
import useDisplayMessage from "../../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../../common/lib/KaAlert";
import { driverFacingErrorMessage } from "../../../common/userFacingMessages/userFacingMessages";
import ScanButton from "../../../dev/ScanButton";
import useReceivingToManualDelivery from "../../../requests/Receiving/useReceivingToManualDelivery";
import { ReceivingType } from "../../StageOrderFlow/StageOrderTypes";
import DataMatrixDisplay from "./DataMatrixDisplay";

interface BayDirectionsProps {
  spoId: string,
  siteId: string,
  bayName?: string,
  showDataMatrix: boolean,
  purchaseOrderNumber: string,
  devToolItems?: React.ReactNode,
  receivingType?: ReceivingType,
  deliveryComplete: () => void,
}

const ReceivingBayDirections = (props: BayDirectionsProps) => {
  const { _ } = useLingui();

  const isReturnToScale = props.receivingType === ReceivingType.Scale;
  const isAutomated = props.receivingType === ReceivingType.BulkReceiving;
  const isManual = props.receivingType === ReceivingType.Loadpass || props.receivingType === null;
  const showDataMatrix = isReturnToScale || (isAutomated && props.showDataMatrix);

  const helperText = isReturnToScale ? _(msg`Scan at outbound terminal after delivery`) : "";

  const displayMessage = useDisplayMessage();

  const toManualDelivery = useReceivingToManualDelivery(props.spoId, {
    onSuccess: resp => props.deliveryComplete(),
    onError: err => displayMessage.fail(_(driverFacingErrorMessage(err))),
  });

  return <Container
    title={props.bayName ? _(msg`Proceed to ${props.bayName}`) : helperText}
    subtitle={props.bayName ? helperText : undefined}
    devToolItems={
      <>
        {isReturnToScale
          && <ScanButton location='AtReceivingOutbound' siteId={props.siteId} stagedPurchaseOrderId={props.spoId} />
        }
        {isAutomated
          && <ScanButton location='ReceivingComplete' stagedPurchaseOrderId={props.spoId} />
        }
        {props.devToolItems}
      </>
    }>
    <div className="my-5 px-5 h-75 ">
      <KaAlert
        displayMessage={displayMessage.message}
        onClose={displayMessage.clear} />
      {
        showDataMatrix
          ? <DataMatrixDisplay stagedPurchaseOrderId={props.spoId} purchaseOrderNumber={props.purchaseOrderNumber} />
          : <div className="position-relative">
            <div className="text-center mt-10">
              <h2><Trans>You will be prompted for signature after delivery is completed.</Trans></h2>
            </div>
          </div>
      }
      {
        isManual &&
        <div className="position-relative">
          <div className="text-center mt-10">
            <h2><Trans>Press 'Delivery Complete' after you have finished unloading.</Trans></h2>
          </div>
        </div>
      }
    </div>
    {isManual && <Footer
      nextButton={
        <FooterButton
          spinning={toManualDelivery.isLoading}
          onClick={() => toManualDelivery.request()}>
          <Trans>Delivery Complete</Trans>
        </FooterButton>
      }
    />}
  </Container>
}

export default ReceivingBayDirections;
